import { useState } from 'react';

import { queryClient } from '@api/queryClient';
import { EditableSession, EventType } from 'types/event.types';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { EventEstimatedAttendanceModal } from './EventEstimatedAttendanceModal';

interface EstimatedAttendanceModalProps
  extends React.ComponentPropsWithoutRef<typeof EventEstimatedAttendanceModal> {}

interface UseEstimatedAttendanceModalOptions {
  session?: EditableSession;
}

interface UseEstimatedAttendanceModalValue {
  modalProps: EstimatedAttendanceModalProps;
  open: () => void;
}

const useEstimatedAttendanceModal = (
  options: UseEstimatedAttendanceModalOptions
): UseEstimatedAttendanceModalValue => {
  const event = useEditableEvent();

  const [isOpen, setIsOpen] = useState(false);

  const eventOrSession = options.session ?? event;

  const modalProps: EstimatedAttendanceModalProps = {
    sessionOrEventId: eventOrSession.id!,
    parentEventId: event.id!.toString(),
    isEventMultiSession: eventOrSession.eventType === EventType.SESSION,
    isUsingEstimatedAttendees: eventOrSession.isUsingEstimatedAttendees,
    estimatedAttendeesCount: eventOrSession.estimatedAttendees,
    hasUnlimitedSpots: eventOrSession.virtualEventInfo?.hasUnlimitedSpots,
    timezoneCode: eventOrSession.timezoneCode,
    isEstimatedAttendanceModalOpen: isOpen,
    setIsEstimatedAttendanceModalOpen: setIsOpen,
    refetchEvent: () => {
      queryClient.refetchQueries({
        queryKey: eventEditorQueryKeys.event(event.id!)
      });
      queryClient.refetchQueries({
        queryKey: eventEditorQueryKeys.eventSessions(event.id!)
      });
    }
  };

  return {
    modalProps,
    open: () => setIsOpen(true)
  };
};

export { useEstimatedAttendanceModal };
